import { Route, Routes } from "react-router-dom";

import { AuthProvider } from "@/auth/provider";
import { config } from "@/config";
import {
  Authenticated,
  RequireClientAdmin,
  RequireMedicusStaff,
  RequirePermission,
} from "@/auth/guards";
import { CallbackPage } from "@/pages/CallbackPage";
import { BiomarkerDetailPage } from "@/pages/BiomarkerDetailPage";
import { CatalogDetailPage } from "@/pages/CatalogDetailPage";
import { CatalogListPage } from "@/pages/CatalogListPage";
import { ClientConfigPage } from "@/pages/ClientConfigPage";
import { ContentDetailPage } from "@/pages/ContentDetailPage";
import { ContentListPage } from "@/pages/ContentListPage";
import { HomePage } from "@/pages/HomePage";
import { NewBiomarkerPage } from "@/pages/NewBiomarkerPage";
import { RuleDetailPage } from "@/pages/RuleDetailPage";
import { RulesListPage } from "@/pages/RulesListPage";
import { SearchPage } from "@/pages/SearchPage";
import { CitationEditorPage } from "@/pages/CitationEditorPage";
import { SimulatePage } from "@/pages/SimulatePage";
import { SpreadsheetUploadPage } from "@/pages/SpreadsheetUploadPage";
import { LoginPage } from "@/pages/LoginPage";
import { LogoutPage } from "@/pages/LogoutPage";
import { SignedOutPage } from "@/pages/SignedOutPage";
import { AdminAuditPage } from "@/pages/admin/AdminAuditPage";
import { AdminFlagsPage } from "@/pages/admin/AdminFlagsPage";
import { BrandingPage } from "@/pages/branding/BrandingPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { PortalApprovalsPage } from "@/pages/portal/PortalApprovalsPage";
import { PortalAuditPage } from "@/pages/portal/PortalAuditPage";
import { PortalMessagesPage } from "@/pages/portal/PortalMessagesPage";
import { PortalReportReviewPage } from "@/pages/portal/PortalReportReviewPage";
import { PortalBrandedPreviewPage } from "@/pages/portal/PortalBrandedPreviewPage";
import { PortalReportsPage } from "@/pages/portal/PortalReportsPage";
import { PortalHomePage } from "@/pages/portal/PortalHomePage";
import { PortalUsersPage } from "@/pages/portal/PortalUsersPage";
import { MySignaturePage } from "@/pages/portal/MySignaturePage";
import { PortalPermissionsUnavailablePage } from "@/pages/portal/PortalPermissionsUnavailablePage";
import { TenantMisconfiguredPage } from "@/pages/portal/TenantMisconfiguredPage";

/**
 * App router. Phase 2 (ADR-0041) wires the IAM PKCE auth shell:
 *
 * - `/login` and `/auth/callback` are public — no `<Authenticated>` wrap.
 * - `/logout` is public so unauthenticated visitors can still trigger
 *   IAM end-session cleanup.
 * - `/` (and every future authenticated surface) wraps in `<Authenticated>`,
 *   which redirects to `/login?next=<path>` if no token is held.
 * - `*` falls through to a 404.
 *
 * Subsequent phases (per `docs/planning/onboarding-web-ui-frontend-chip-plan.md`)
 * add `/catalogs`, `/catalogs/:org`, `/catalogs/:org/branch/:branch`, and the
 * per-operation children — each wrapped in `<Authenticated>` and gated by
 * `<RequirePermission>` where the JWT permission claim drives surface
 * visibility (ADR-0026).
 */
export default function App(): JSX.Element {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<CallbackPage />} />
        <Route path="/logout" element={<LogoutPage />} />
        {/* Public, and deliberately inert: no redirect to IAM until the user asks. */}
        <Route path="/signed-out" element={<SignedOutPage />} />
        <Route
          path="/"
          element={
            <Authenticated>
              <HomePage />
            </Authenticated>
          }
        />
        <Route
          path="/catalogs"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:read" fallback={<NotFoundPage />}>
                <CatalogListPage />
              </RequirePermission>
            </Authenticated>
          }
        />
        <Route
          path="/catalogs/:org"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:read" fallback={<NotFoundPage />}>
                <CatalogDetailPage />
              </RequirePermission>
            </Authenticated>
          }
        />
        {/* docs#150: ADR-0064 report presentation. The route needs catalog:read and shows
            the served config. The editor inside needs catalog:author, and onboarding-api
            refuses validate and submit without it, so hiding it is not the control. */}
        <Route
          path="/catalogs/:org/config"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:read" fallback={<NotFoundPage />}>
                <ClientConfigPage />
              </RequirePermission>
            </Authenticated>
          }
        />
        <Route
          path="/catalogs/:org/new-biomarker"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:author" fallback={<NotFoundPage />}>
                <NewBiomarkerPage />
              </RequirePermission>
            </Authenticated>
          }
        />
        <Route
          path="/catalogs/:org/branch/:branch/new-biomarker"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:author" fallback={<NotFoundPage />}>
                <NewBiomarkerPage />
              </RequirePermission>
            </Authenticated>
          }
        />
        <Route
          path="/catalogs/:org/spreadsheet"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:author" fallback={<NotFoundPage />}>
                <SpreadsheetUploadPage />
              </RequirePermission>
            </Authenticated>
          }
        />
        <Route
          path="/catalogs/:org/branch/:branch/spreadsheet"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:author" fallback={<NotFoundPage />}>
                <SpreadsheetUploadPage />
              </RequirePermission>
            </Authenticated>
          }
        />
        <Route
          path="/catalogs/:org/biomarker/:symbol"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:read" fallback={<NotFoundPage />}>
                <BiomarkerDetailPage />
              </RequirePermission>
            </Authenticated>
          }
        />
        <Route
          path="/catalogs/:org/branch/:branch"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:read" fallback={<NotFoundPage />}>
                <CatalogDetailPage />
              </RequirePermission>
            </Authenticated>
          }
        />
        <Route
          path="/catalogs/:org/branch/:branch/biomarker/:symbol"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:read" fallback={<NotFoundPage />}>
                <BiomarkerDetailPage />
              </RequirePermission>
            </Authenticated>
          }
        />
        <Route
          path="/authoring/rules"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:read" fallback={<NotFoundPage />}>
                <RulesListPage />
              </RequirePermission>
            </Authenticated>
          }
        />
        <Route
          path="/authoring/rules/:region/:insightId"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:read" fallback={<NotFoundPage />}>
                <RuleDetailPage />
              </RequirePermission>
            </Authenticated>
          }
        />
        <Route
          path="/authoring/content"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:read" fallback={<NotFoundPage />}>
                <ContentListPage />
              </RequirePermission>
            </Authenticated>
          }
        />
        <Route
          path="/authoring/content/:insightId"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:read" fallback={<NotFoundPage />}>
                <ContentDetailPage />
              </RequirePermission>
            </Authenticated>
          }
        />
        <Route
          path="/authoring/search"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:read" fallback={<NotFoundPage />}>
                <SearchPage />
              </RequirePermission>
            </Authenticated>
          }
        />
        <Route
          path="/authoring/simulate"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:read" fallback={<NotFoundPage />}>
                <SimulatePage />
              </RequirePermission>
            </Authenticated>
          }
        />
        <Route
          path="/authoring/citations"
          element={
            <Authenticated>
              <RequirePermission perm="catalog:read" fallback={<NotFoundPage />}>
                <CitationEditorPage />
              </RequirePermission>
            </Authenticated>
          }
        />
        {/* Client Portal, tenant-scoped. `RequireClientAdmin` gates on the
            SERVER-RESOLVED permission each route's backend enforces
            (clients:manage, or audit:read for /portal/audit), never the token's
            role claim. It keeps "not a tenant admin" (404), "admin with no
            organization claim" (tenant misconfigured) and "could not resolve
            permissions" (unavailable) apart, because they mean different things. */}
        <Route
          path="/portal"
          element={
            <Authenticated>
              <RequireClientAdmin
                fallback={<NotFoundPage />}
                misconfigured={<TenantMisconfiguredPage />}
                unavailable={<PortalPermissionsUnavailablePage />}
              >
                <PortalHomePage />
              </RequireClientAdmin>
            </Authenticated>
          }
        />
        <Route
          path="/portal/users"
          element={
            <Authenticated>
              <RequireClientAdmin
                fallback={<NotFoundPage />}
                misconfigured={<TenantMisconfiguredPage />}
                unavailable={<PortalPermissionsUnavailablePage />}
              >
                <PortalUsersPage />
              </RequireClientAdmin>
            </Authenticated>
          }
        />
        <Route
          path="/portal/approvals"
          element={
            <Authenticated>
              <RequireClientAdmin
                fallback={<NotFoundPage />}
                misconfigured={<TenantMisconfiguredPage />}
                unavailable={<PortalPermissionsUnavailablePage />}
              >
                <PortalApprovalsPage />
              </RequireClientAdmin>
            </Authenticated>
          }
        />
        <Route
          path="/portal/audit"
          element={
            <Authenticated>
              <RequireClientAdmin
                perm="audit:read"
                fallback={<NotFoundPage />}
                misconfigured={<TenantMisconfiguredPage />}
                unavailable={<PortalPermissionsUnavailablePage />}
              >
                <PortalAuditPage />
              </RequireClientAdmin>
            </Authenticated>
          }
        />
        {/* docs#239 note 17369 item 4. Registered only when onboarding-api says its
            /api/v1/portal/lis/messages route exists (SR_HELD_MESSAGE_REVIEW_ENABLED), so
            with the flag off the path falls through to not-found, as the Reports page does,
            rather than opening a page that fails on a raw 404. */}
        {config.heldMessageReviewEnabled ? (
          <Route
            path="/portal/lis-messages"
            element={
              <Authenticated>
                <RequireClientAdmin
                  fallback={<NotFoundPage />}
                  misconfigured={<TenantMisconfiguredPage />}
                  unavailable={<PortalPermissionsUnavailablePage />}
                >
                  <PortalMessagesPage />
                </RequireClientAdmin>
              </Authenticated>
            }
          />
        ) : null}
        {/* Gate B M1 S7, the lab's Reports page and its read-only review view. Registered
            only when onboarding-api says its /api/v1/portal/reports routes exist
            (SR_PORTAL_REPORTS_ENABLED), so with the flag off both paths fall through to
            not-found. Guarded like the Messages page, on the server-resolved permission
            the BFF enforces, which a lab's doctors hold through their Clinician group. */}
        {config.portalReportsEnabled ? (
          <Route
            path="/portal/reports"
            element={
              <Authenticated>
                <RequireClientAdmin
                  perm="reports:clinical:read"
                  fallback={<NotFoundPage />}
                  misconfigured={<TenantMisconfiguredPage />}
                  unavailable={<PortalPermissionsUnavailablePage />}
                >
                  <PortalReportsPage />
                </RequireClientAdmin>
              </Authenticated>
            }
          />
        ) : null}
        {config.portalReportsEnabled ? (
          <Route
            path="/portal/reports/:reportId"
            element={
              <Authenticated>
                <RequireClientAdmin
                  perm="reports:clinical:read"
                  fallback={<NotFoundPage />}
                  misconfigured={<TenantMisconfiguredPage />}
                  unavailable={<PortalPermissionsUnavailablePage />}
                >
                  <PortalReportReviewPage />
                </RequireClientAdmin>
              </Authenticated>
            }
          />
        ) : null}
        {/* Plan B7: the patient page in the lab's saved branding, linked from the review
            page. Needs both flags, and the same server-resolved key as the review page: the
            BFF serves a doctor only their own organisation's saved record. */}
        {config.portalReportsEnabled && config.brandingEnabled ? (
          <Route
            path="/portal/reports/:reportId/branded-preview"
            element={
              <Authenticated>
                <RequireClientAdmin
                  perm="reports:clinical:read"
                  fallback={<NotFoundPage />}
                  misconfigured={<TenantMisconfiguredPage />}
                  unavailable={<PortalPermissionsUnavailablePage />}
                >
                  <PortalBrandedPreviewPage />
                </RequireClientAdmin>
              </Authenticated>
            }
          />
        ) : null}
        {/* Gate B white-labelling. Registered only when onboarding-api says /api/v1/branding
            exists (SR_BRANDING_ENABLED), so with the flag off all three paths fall through to
            not-found. Each is guarded on the server-resolved key its BFF routes check: a lab's
            client admin brands their own organisation and the ones under it, a lab doctor
            manages their own signature, and Medicus staff brand any lab from the admin area.
            Who may brand which organisation is decided by the orchestrator, not here. */}
        {config.brandingEnabled ? (
          <Route
            path="/portal/branding"
            element={
              <Authenticated>
                <RequireClientAdmin
                  perm="branding:manage"
                  fallback={<NotFoundPage />}
                  misconfigured={<TenantMisconfiguredPage />}
                  unavailable={<PortalPermissionsUnavailablePage />}
                >
                  <BrandingPage mode="portal" />
                </RequireClientAdmin>
              </Authenticated>
            }
          />
        ) : null}
        {config.brandingEnabled ? (
          <Route
            path="/portal/signature"
            element={
              <Authenticated>
                <RequireClientAdmin
                  perm="branding:signature"
                  fallback={<NotFoundPage />}
                  misconfigured={<TenantMisconfiguredPage />}
                  unavailable={<PortalPermissionsUnavailablePage />}
                >
                  <MySignaturePage />
                </RequireClientAdmin>
              </Authenticated>
            }
          />
        ) : null}
        {config.brandingEnabled ? (
          <Route
            path="/admin/branding"
            element={
              <Authenticated>
                <RequirePermission perm="branding:admin" fallback={<NotFoundPage />}>
                  <BrandingPage mode="admin" />
                </RequirePermission>
              </Authenticated>
            }
          />
        ) : null}
        {/* Admin UI, Medicus staff only (docs#239 item 6): audit:read AND no
            organisation claim. A lab admin also holds audit:read, and her page is
            /portal/audit. The fleet/tenant scoping and PHI gate are enforced
            downstream (BFF + audit service). */}
        <Route
          path="/admin/audit"
          element={
            <Authenticated>
              <RequireMedicusStaff perm="audit:read" fallback={<NotFoundPage />}>
                <AdminAuditPage />
              </RequireMedicusStaff>
            </Authenticated>
          }
        />
        <Route
          path="/admin/flags"
          element={
            <Authenticated>
              <RequireMedicusStaff perm="audit:read" fallback={<NotFoundPage />}>
                <AdminFlagsPage />
              </RequireMedicusStaff>
            </Authenticated>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AuthProvider>
  );
}
